import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShoppingBag, Plus, Trash2, Loader2, Upload, Star, ExternalLink,
  Pencil, X, Image as ImageIcon,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  fetchCuratedGear, upsertCuratedGear, deleteCuratedGear,
  type CuratedGearProduct, type GearReview,
} from '@/lib/api';
import type { Tenant } from '@/types';

const ICON_OPTIONS = [
  '', 'sun', 'water', 'plug', 'backpack', 'shirt',
  'footwear', 'pill', 'bug', 'umbrella', 'camera', 'package',
];

const MAX_IMG = 5 * 1024 * 1024;

const input =
  'w-full px-3 py-2 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 placeholder-surface-500 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10';
const label = 'block text-xs font-medium text-surface-600 mb-1';

function blank(): CuratedGearProduct {
  return {
    destination: '', product_name: '', brand: '', image_url: '', price: '',
    used_price: '', rating: null, review_count: null,
    testimonial: '', testimonial_author: '', reviews: [],
    why: '', buy_url: '', icon: '', is_active: true, sort_order: 0,
  };
}

export function GearPicksPage({ tenant }: { tenant: Tenant }) {
  const [list, setList] = useState<CuratedGearProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CuratedGearProduct | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setList(await fetchCuratedGear(tenant.id)); }
    catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    if (!editing.product_name.trim()) { setError('Product name is required.'); return; }
    if (!editing.buy_url.trim()) { setError('Buy link is required.'); return; }
    setSaving(true); setError(null);
    try {
      await upsertCuratedGear(tenant.id, {
        ...editing,
        destination: editing.destination?.trim() || null,
        rating: editing.rating === null || Number.isNaN(editing.rating) ? null : editing.rating,
        review_count: editing.review_count === null || Number.isNaN(editing.review_count) ? null : editing.review_count,
        reviews: (editing.reviews ?? [])
          .filter(r => r.author.trim() || r.text.trim())
          .map(r => ({ author: r.author.trim(), text: r.text.trim(), rating: r.rating === null || Number.isNaN(r.rating) ? null : r.rating })),
      });
      setEditing(null);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  const remove = async (id?: string) => {
    if (!id) return;
    if (!confirm('Delete this product?')) return;
    try { await deleteCuratedGear(id, tenant.id); await load(); }
    catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <ShoppingBag className="w-6 h-6 text-brand-500" />
          <div>
            <h1 className="text-xl font-semibold text-surface-900">Gear Picks</h1>
            <p className="text-sm text-surface-600">
              Curated products shown at the top of the Gear &amp; Essentials shelf in the app.
            </p>
          </div>
        </div>
        {!editing && (
          <button
            onClick={() => { setError(null); setEditing(blank()); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add product
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-lg border border-red-300 bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {editing && (
        <ProductEditor
          tenantId={tenant.id}
          value={editing}
          onChange={setEditing}
          onSave={save}
          onCancel={() => { setEditing(null); setError(null); }}
          saving={saving}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-surface-600 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : list.length === 0 && !editing ? (
        <div className="text-center py-14 border border-dashed border-surface-300 rounded-xl">
          <ShoppingBag className="w-8 h-8 text-surface-400 mx-auto mb-2" />
          <p className="text-surface-600 text-sm">No gear picks yet. Add your first product above.</p>
        </div>
      ) : (
        <div className="space-y-2 mt-5">
          {list.map((p) => (
            <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-surface-300 bg-surface-100">
              <div className="w-14 h-14 rounded-lg bg-surface-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                {p.image_url
                  ? <img src={p.image_url} alt={p.product_name} className="w-full h-full object-cover" />
                  : <ImageIcon className="w-5 h-5 text-surface-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-surface-900 truncate">{p.product_name}</span>
                  {!p.is_active && <span className="text-[10px] uppercase tracking-wide text-surface-500 border border-surface-300 rounded px-1.5 py-0.5">hidden</span>}
                </div>
                <div className="flex items-center gap-2 text-xs text-surface-600 mt-0.5">
                  {p.destination ? <span>{p.destination}</span> : <span className="italic">all trips</span>}
                  {p.price && <span>· {p.price}</span>}
                  {p.rating != null && <span className="inline-flex items-center gap-0.5">· <Star className="w-3 h-3 text-amber-500" /> {p.rating}{p.review_count != null ? ` (${p.review_count})` : ''}</span>}
                </div>
              </div>
              <a href={p.buy_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-surface-200 text-surface-500" title="Open buy link"><ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => { setError(null); setEditing(p); }} className="p-2 rounded-lg hover:bg-surface-200 text-surface-500" title="Edit"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductEditor({
  tenantId, value, onChange, onSave, onCancel, saving,
}: {
  tenantId: string;
  value: CuratedGearProduct;
  onChange: (v: CuratedGearProduct) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const set = (patch: Partial<CuratedGearProduct>) => onChange({ ...value, ...patch });

  const upload = async (file: File) => {
    setUpErr(null);
    if (!file.type.startsWith('image/')) { setUpErr('Pick an image file.'); return; }
    if (file.size > MAX_IMG) { setUpErr('Max 5 MB.'); return; }
    setUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
      const path = `${tenantId}/gear/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('tenant-branding').upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
      if (error) throw error;
      const { data } = supabase.storage.from('tenant-branding').getPublicUrl(path);
      set({ image_url: data?.publicUrl || '' });
    } catch (e) { setUpErr((e as Error).message); }
    finally { setUploading(false); }
  };

  return (
    <div className="mb-6 p-5 rounded-xl border border-surface-300 bg-surface-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Image upload */}
        <div className="md:col-span-2 flex items-center gap-4">
          <div className="w-24 h-24 rounded-lg bg-surface-200 overflow-hidden flex items-center justify-center flex-shrink-0">
            {value.image_url
              ? <img src={value.image_url} alt="" className="w-full h-full object-cover" />
              : <ImageIcon className="w-6 h-6 text-surface-400" />}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 px-3 py-2 border border-surface-300 rounded-lg text-sm text-surface-700 hover:bg-surface-200 disabled:opacity-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {value.image_url ? 'Replace image' : 'Upload image'}
            </button>
            {value.image_url && <button type="button" onClick={() => set({ image_url: '' })} className="ml-2 text-xs text-surface-500 hover:text-red-500">Remove</button>}
            {upErr && <p className="text-xs text-red-600 mt-1">{upErr}</p>}
            <p className="text-xs text-surface-500 mt-1">PNG / JPG / WebP, max 5 MB.</p>
          </div>
        </div>

        <div>
          <label className={label}>Product name *</label>
          <input className={input} value={value.product_name} onChange={(e) => set({ product_name: e.target.value })} placeholder="Sunset Hat — Women's" />
        </div>
        <div>
          <label className={label}>Brand</label>
          <input className={input} value={value.brand ?? ''} onChange={(e) => set({ brand: e.target.value })} placeholder="Sunday Afternoons" />
        </div>
        <div>
          <label className={label}>Destination <span className="text-surface-400">(blank = all trips)</span></label>
          <input className={input} value={value.destination ?? ''} onChange={(e) => set({ destination: e.target.value })} placeholder="Thailand" />
        </div>
        <div>
          <label className={label}>Buy link *</label>
          <input className={input} value={value.buy_url} onChange={(e) => set({ buy_url: e.target.value })} placeholder="https://www.amazon.com/dp/...?tag=yourtag-20" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Price</label>
            <input className={input} value={value.price ?? ''} onChange={(e) => set({ price: e.target.value })} placeholder="$46" />
          </div>
          <div>
            <label className={label}>Used price <span className="text-surface-400">(optional)</span></label>
            <input className={input} value={value.used_price ?? ''} onChange={(e) => set({ used_price: e.target.value })} placeholder="$29" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Rating</label>
            <input className={input} type="number" min={0} max={5} step={0.1} value={value.rating ?? ''} onChange={(e) => set({ rating: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="4.5" />
          </div>
          <div>
            <label className={label}>Reviews</label>
            <input className={input} type="number" min={0} step={1} value={value.review_count ?? ''} onChange={(e) => set({ review_count: e.target.value === '' ? null : parseInt(e.target.value, 10) })} placeholder="103" />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className={label}>Why it's recommended for this trip</label>
          <textarea className={input} rows={2} value={value.why ?? ''} onChange={(e) => set({ why: e.target.value })} placeholder="Packable, breathable brim that actually shades you from strong tropical sun." />
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className={label + ' mb-0'}>Reviews</label>
            <button
              type="button"
              onClick={() => set({ reviews: [...(value.reviews ?? []), { author: '', rating: null, text: '' }] })}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-500"
            >
              <Plus className="w-3.5 h-3.5" /> Add review
            </button>
          </div>
          {(value.reviews ?? []).length === 0 && (
            <p className="text-xs text-surface-500 mb-2">No reviews yet — add customer-style quotes shown on the product card.</p>
          )}
          <div className="space-y-3">
            {(value.reviews ?? []).map((rv, i) => {
              const update = (patch: Partial<GearReview>) => {
                const next = [...(value.reviews ?? [])];
                next[i] = { ...next[i], ...patch };
                set({ reviews: next });
              };
              const removeRv = () => set({ reviews: (value.reviews ?? []).filter((_, j) => j !== i) });
              return (
                <div key={i} className="p-3 rounded-lg border border-surface-300 bg-surface-50">
                  <div className="flex items-center gap-2 mb-2">
                    <input className={input + ' flex-1'} value={rv.author} onChange={(e) => update({ author: e.target.value })} placeholder="Reviewer name" />
                    <input className={input + ' w-24'} type="number" min={0} max={5} step={0.5} value={rv.rating ?? ''} onChange={(e) => update({ rating: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="★ 5" />
                    <button type="button" onClick={removeRv} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Remove review"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <textarea className={input} rows={2} value={rv.text} onChange={(e) => update({ text: e.target.value })} placeholder="Best sun hats — packable, breathable, doesn't break the bank. A must-have." />
                </div>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Icon</label>
            <select className={input} value={value.icon ?? ''} onChange={(e) => set({ icon: e.target.value })}>
              {ICON_OPTIONS.map(o => <option key={o} value={o}>{o === '' ? 'auto' : o}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Sort order</label>
            <input className={input} type="number" step={1} value={value.sort_order} onChange={(e) => set({ sort_order: parseInt(e.target.value, 10) || 0 })} />
          </div>
        </div>
        <div className="md:col-span-2 flex items-center gap-2">
          <input id="gear-active" type="checkbox" checked={value.is_active} onChange={(e) => set({ is_active: e.target.checked })} />
          <label htmlFor="gear-active" className="text-sm text-surface-700">Active (visible in the app)</label>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-5">
        <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 disabled:opacity-50">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save product
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-4 py-2 border border-surface-300 rounded-lg text-sm text-surface-700 hover:bg-surface-200">
          <X className="w-4 h-4" /> Cancel
        </button>
      </div>
    </div>
  );
}
